/**
 * ToolCallbackHandler — bridges Gemini function-call responses back to
 * the Cogo ActionFlow engine so the model can invoke real actions
 * (REST calls, navigation, data fetches, etc.) during a conversation loop.
 *
 * Lifecycle:
 *   1. Gemini returns a `functionCall` part
 *   2. `handle()` dispatches the call to the Cogo action executor
 *   3. `buildFunctionResponseContent()` wraps the result as a Gemini
 *      `functionResponse` content block
 *   4. The content is appended to the conversation and sent back to Gemini
 */

import type { GeminiContent } from './gemini-client'

type ActionExecutor = (
  actionName: string,
  params: Record<string, unknown>,
) => Promise<unknown>

export class ToolCallbackHandler {
  private executeAction: ActionExecutor

  constructor(executeAction: ActionExecutor) {
    this.executeAction = executeAction
  }

  /**
   * Execute a Cogo action on behalf of the Gemini model and return a
   * structured result suitable for round-tripping back into the
   * conversation.
   */
  async handle(functionCall: {
    name: string
    args: Record<string, unknown>
  }): Promise<{ name: string; response: Record<string, unknown> }> {
    try {
      const result = await this.executeAction(functionCall.name, functionCall.args)
      return {
        name: functionCall.name,
        response: (typeof result === 'object' && result !== null
          ? result
          : { result }) as Record<string, unknown>,
      }
    } catch (err) {
      return {
        name: functionCall.name,
        response: { error: String(err) },
      }
    }
  }

  /**
   * Wrap a tool result as a Gemini `functionResponse` content block
   * that can be appended to the `contents` array for the next
   * generateContent call.
   */
  buildFunctionResponseContent(
    name: string,
    response: Record<string, unknown>,
  ): GeminiContent {
    return {
      role: 'user',
      parts: [{ functionResponse: { name, response } }],
    }
  }
}
